import React, { useState, useEffect } from 'react';
import { SiteHeader, SiteFooter, AGENCY_CONTACT } from '../../components/website/SharedLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Download, FileText, Phone, Mail, Eye, Smartphone, Monitor,
  CheckCircle, Star, Clock, Users, ArrowRight, ExternalLink
} from 'lucide-react';
import { cn } from '../../lib/utils';

const BrochurePage = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/website/brochure/download`;

  useEffect(() => {
    // Detect mobile device
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleDownload = () => {
    window.open(pdfUrl, '_blank');
  };

  const handlePdfLoad = () => {
    setPdfLoaded(true);
    setPdfError(false);
  };

  const handlePdfError = () => {
    setPdfError(true);
    setPdfLoaded(false);
  };

  // Key features to highlight
  const features = [
    { icon: Star, title: "Services premium", desc: "Création web, graphique, vidéo et stratégie" },
    { icon: Users, title: "Équipe experte", desc: "15+ ans d'expérience en communication" },
    { icon: CheckCircle, title: "Résultats prouvés", desc: "200+ projets réalisés avec succès" },
    { icon: Clock, title: "Réactivité", desc: "Réponse sous 24h garantie" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 mb-4">
            <FileText className="w-3 h-3 mr-1" />
            Brochure commerciale
          </Badge>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Découvrez <span className="text-[#00d4aa]">MSC L'Agence</span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            Notre brochure présente nos services, nos réalisations et notre approche 
            pour booster votre communication digitale et print.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleDownload}
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-lg shadow-cyan-500/30"
            >
              <Download className="w-5 h-5 mr-2" />
              Télécharger la brochure PDF
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="border-white/30 text-white hover:bg-white/10"
              onClick={() => window.location.href = `tel:${AGENCY_CONTACT.phone.replace(/\s/g, '')}`}
            >
              <Phone className="w-5 h-5 mr-2" />
              Nous appeler
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {features.map((feature, idx) => (
              <Card key={idx} className="text-center border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="w-12 h-12 mx-auto rounded-full bg-cyan-100 flex items-center justify-center mb-3">
                    <feature.icon className="w-6 h-6 text-cyan-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm mb-1">{feature.title}</h3>
                  <p className="text-xs text-slate-500">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PDF Viewer Section */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Mobile View - Download Card */}
          {isMobile ? (
            <Card className="border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-white">
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mb-6 shadow-lg shadow-cyan-500/30">
                  <FileText className="w-10 h-10 text-white" />
                </div>
                
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Brochure MSC L'Agence 2025
                </h2>
                <p className="text-slate-600 mb-6">
                  6 pages • Format A4 • PDF haute qualité
                </p>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 text-amber-700 text-sm">
                    <Smartphone className="w-4 h-4" />
                    <span>Sur mobile, téléchargez le PDF pour une meilleure lecture</span>
                  </div>
                </div>
                
                <Button 
                  onClick={handleDownload}
                  size="lg"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Télécharger la brochure
                </Button>
                
                <p className="text-xs text-slate-400 mt-4">
                  Le PDF s'ouvrira dans votre lecteur ou sera téléchargé
                </p>
              </CardContent>
            </Card>
          ) : (
            /* Desktop View - PDF Embed */
            <div className="space-y-4">
              {/* Toolbar */}
              <div className="bg-slate-100 rounded-t-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-cyan-600" />
                  <span className="font-medium text-slate-800">Brochure MSC L'Agence 2025</span>
                  <Badge variant="secondary">PDF • 6 pages</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleDownload}>
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger
                  </Button>
                </div>
              </div>
              
              {/* PDF Container */}
              <div className="bg-slate-800 rounded-b-xl overflow-hidden" style={{ height: '80vh' }}>
                {pdfError ? (
                  <div className="flex flex-col items-center justify-center h-full text-white p-8">
                    <FileText className="w-16 h-16 text-slate-400 mb-4" />
                    <p className="text-lg font-medium mb-2">Aperçu non disponible</p>
                    <p className="text-slate-400 text-sm mb-6 text-center">
                      Votre navigateur ne peut pas afficher le PDF directement.
                    </p>
                    <Button onClick={handleDownload} className="bg-cyan-500 hover:bg-cyan-600">
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger le PDF
                    </Button>
                  </div>
                ) : (
                  <iframe
                    src={`${pdfUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                    title="Brochure MSC L'Agence"
                    className="w-full h-full border-0"
                    onLoad={handlePdfLoad}
                    onError={handlePdfError}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#00d4aa] to-cyan-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Prêt à booster votre communication ?
          </h2>
          <p className="text-white/90 mb-8 text-lg">
            Contactez-nous pour discuter de votre projet
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-[#00d4aa] hover:bg-slate-100"
              onClick={() => window.location.href = `mailto:${AGENCY_CONTACT.email}`}
            >
              <Mail className="w-5 h-5 mr-2" />
              {AGENCY_CONTACT.email}
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10"
              onClick={() => window.location.href = `tel:${AGENCY_CONTACT.phone.replace(/\s/g, '')}`}
            >
              <Phone className="w-5 h-5 mr-2" />
              {AGENCY_CONTACT.phone}
            </Button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default BrochurePage;
